import { useMemo, useState } from 'react';
import { Box, Breadcrumbs, Button, Chip, Grid, Paper, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';
import { ArrowForward as ArrowForwardIcon, TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon, AccountTree as AccountTreeIcon, AutoAwesome as ForecastIcon } from '@mui/icons-material';
import { BarChart, Bar, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from 'recharts';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import { DashboardInfoTip } from './DashboardInfoTip';

type SummaryData = {
  salesAmount: number;
  costAmount: number;
  profit: number;
};

type ProjectedSummaryData = {
  potentialRevenue: number;
  costAmount: number;
  projectedProfit: number;
};

type ChartRow = SummaryData & {
  name: string;
};

type ProjectedChartRow = ProjectedSummaryData & {
  name: string;
};

const POSITIVE_COLOR = '#2e7d32';
const NEGATIVE_COLOR = '#d32f2f';

const ProfitDrilldownChart = ({
  chartRows,
  viewMode,
  selectedDepot,
  onSelectDepot,
  formatTooltipValue,
  t,
}: {
  chartRows: (ChartRow | ProjectedChartRow)[];
  viewMode: 'actual' | 'projected';
  selectedDepot: string | null;
  onSelectDepot: (depot: string) => void;
  formatTooltipValue: (value: number, name: string) => string[];
  t: ReturnType<typeof useTranslation>['t'];
}) => {
  if (chartRows.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 280, color: 'text.secondary' }}>
        <Typography>{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {selectedDepot
            ? t('common:dashboard.profits.byCategory')
            : t('common:dashboard.profits.byDepot')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {selectedDepot
            ? t('common:dashboard.profits.categoryClickHint')
            : t('common:dashboard.profits.depotClickHint')}
        </Typography>
      </Box>
      <DashboardSizedChart height={320} dynamicHeight={chartRows.length * 42}>
        <BarChart
          data={chartRows}
          layout="vertical"
          margin={{ top: 5, right: 25, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis type="number" tickFormatter={formatCompactNumber} />
          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
          <Tooltip formatter={formatTooltipValue} />
          <Bar
            dataKey={viewMode === 'actual' ? 'profit' : 'projectedProfit'}
            radius={[0, 4, 4, 0]}
            onClick={(row: ChartRow | ProjectedChartRow) => {
              if (!selectedDepot) onSelectDepot(row.name);
            }}
            style={{ cursor: selectedDepot ? 'default' : 'pointer' }}
          >
            {chartRows.map((row) => {
              const value = viewMode === 'actual' ? (row as ChartRow).profit : (row as ProjectedChartRow).projectedProfit;
              return (
                <Cell key={row.name} fill={value >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR} />
              );
            })}
          </Bar>
        </BarChart>
      </DashboardSizedChart>
    </>
  );
};

const formatCompactNumber = (value: number) => {
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toFixed(0);
};

const ProfitDrilldown = () => {
  const { salesProfitBreakdown, projectedProfitBreakdown, sectionLoading, currency } = useDashboard();
  const { t } = useTranslation();
  const [selectedDepot, setSelectedDepot] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'actual' | 'projected'>('actual');

  const globalSummary = useMemo(() => {
    return salesProfitBreakdown.reduce<SummaryData>((acc, row) => ({
      salesAmount: acc.salesAmount + row.salesAmount,
      costAmount: acc.costAmount + row.costAmount,
      profit: acc.profit + row.profit,
    }), { salesAmount: 0, costAmount: 0, profit: 0 });
  }, [salesProfitBreakdown]);

  const depotRows = useMemo(() => {
    const depotMap = new Map<string, SummaryData>();
    salesProfitBreakdown.forEach((row) => {
      const current = depotMap.get(row.depot) || { salesAmount: 0, costAmount: 0, profit: 0 };
      depotMap.set(row.depot, {
        salesAmount: current.salesAmount + row.salesAmount,
        costAmount: current.costAmount + row.costAmount,
        profit: current.profit + row.profit,
      });
    });

    return Array.from(depotMap.entries())
      .map(([name, values]) => ({ name, ...values }))
      .sort((left, right) => right.profit - left.profit);
  }, [salesProfitBreakdown]);

  const categoryRows = useMemo(() => {
    if (!selectedDepot) return [];

    const categoryMap = new Map<string, SummaryData>();
    salesProfitBreakdown
      .filter((row) => row.depot === selectedDepot)
      .forEach((row) => {
        const current = categoryMap.get(row.category) || { salesAmount: 0, costAmount: 0, profit: 0 };
        categoryMap.set(row.category, {
          salesAmount: current.salesAmount + row.salesAmount,
          costAmount: current.costAmount + row.costAmount,
          profit: current.profit + row.profit,
        });
      });

    return Array.from(categoryMap.entries())
      .map(([name, values]) => ({ name, ...values }))
      .sort((left, right) => right.profit - left.profit);
  }, [salesProfitBreakdown, selectedDepot]);

  // Projected profit summaries
  const projectedGlobalSummary = useMemo(() => {
    return projectedProfitBreakdown.reduce<ProjectedSummaryData>((acc, row) => ({
      potentialRevenue: acc.potentialRevenue + row.potentialRevenue,
      costAmount: acc.costAmount + row.costAmount,
      projectedProfit: acc.projectedProfit + row.projectedProfit,
    }), { potentialRevenue: 0, costAmount: 0, projectedProfit: 0 });
  }, [projectedProfitBreakdown]);

  const projectedDepotRows = useMemo(() => {
    const depotMap = new Map<string, ProjectedSummaryData>();
    projectedProfitBreakdown.forEach((row) => {
      const current = depotMap.get(row.depot) || { potentialRevenue: 0, costAmount: 0, projectedProfit: 0 };
      depotMap.set(row.depot, {
        potentialRevenue: current.potentialRevenue + row.potentialRevenue,
        costAmount: current.costAmount + row.costAmount,
        projectedProfit: current.projectedProfit + row.projectedProfit,
      });
    });

    return Array.from(depotMap.entries())
      .map(([name, values]) => ({ name, ...values }))
      .sort((left, right) => right.projectedProfit - left.projectedProfit);
  }, [projectedProfitBreakdown]);

  const projectedCategoryRows = useMemo(() => {
    if (!selectedDepot) return [];

    const categoryMap = new Map<string, ProjectedSummaryData>();
    projectedProfitBreakdown
      .filter((row) => row.depot === selectedDepot)
      .forEach((row) => {
        const current = categoryMap.get(row.category) || { potentialRevenue: 0, costAmount: 0, projectedProfit: 0 };
        categoryMap.set(row.category, {
          potentialRevenue: current.potentialRevenue + row.potentialRevenue,
          costAmount: current.costAmount + row.costAmount,
          projectedProfit: current.projectedProfit + row.projectedProfit,
        });
      });

    return Array.from(categoryMap.entries())
      .map(([name, values]) => ({ name, ...values }))
      .sort((left, right) => right.projectedProfit - left.projectedProfit);
  }, [projectedProfitBreakdown, selectedDepot]);

  const activeRows = selectedDepot ? categoryRows : depotRows;
  const activeProjectedRows = selectedDepot ? projectedCategoryRows : projectedDepotRows;
  const activeSummary = selectedDepot
    ? categoryRows.reduce<SummaryData>((acc, row) => ({
      salesAmount: acc.salesAmount + row.salesAmount,
      costAmount: acc.costAmount + row.costAmount,
      profit: acc.profit + row.profit,
    }), { salesAmount: 0, costAmount: 0, profit: 0 })
    : globalSummary;

  const activeProjectedSummary = selectedDepot
    ? projectedCategoryRows.reduce<ProjectedSummaryData>((acc, row) => ({
      potentialRevenue: acc.potentialRevenue + row.potentialRevenue,
      costAmount: acc.costAmount + row.costAmount,
      projectedProfit: acc.projectedProfit + row.projectedProfit,
    }), { potentialRevenue: 0, costAmount: 0, projectedProfit: 0 })
    : projectedGlobalSummary;

  const margin = activeSummary.salesAmount !== 0
    ? (activeSummary.profit / activeSummary.salesAmount) * 100
    : 0;

  const projectedMargin = activeProjectedSummary.potentialRevenue !== 0
    ? (activeProjectedSummary.projectedProfit / activeProjectedSummary.potentialRevenue) * 100
    : 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + ` ${currency}`;
  };

  const formatTooltipValue = (value: number, name: string) => {
    const labelMap: Record<string, string> = {
      profit: t('common:dashboard.profits.metrics.profit'),
      projectedProfit: t('common:dashboard.profits.metrics.projected'),
      salesAmount: t('common:dashboard.profits.metrics.sales'),
      potentialRevenue: t('common:dashboard.profits.metrics.potentialRevenue'),
      costAmount: t('common:dashboard.profits.metrics.cost'),
    };

    return [formatCurrency(value), labelMap[name] || name];
  };

  const chartRows = viewMode === 'actual' ? activeRows : activeProjectedRows;

  if (sectionLoading.sales || sectionLoading.inventory) {
    return (
      <DashboardWidget chartHeight={420} scrollable>
        <Skeleton variant="text" width="30%" height={32} />
        <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
          {[1, 2, 3].map((item) => (
            <Grid key={item} size={{ xs: 12, md: 4 }}>
              <Skeleton variant="rectangular" height={92} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" width="100%" height={320} sx={{ borderRadius: 2 }} />
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget
      chartHeight={Math.max(420, chartRows.length * 42)}
      scrollable
      header={
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('common:dashboard.profits.title')}
            </Typography>
            <DashboardInfoTip helpKey="profits.title" />
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {selectedDepot
              ? t('common:dashboard.profits.categoryHint')
              : t('common:dashboard.profits.depotHint')}
          </Typography>
        </Box>
      }
      headerActions={
        <Breadcrumbs separator={<ArrowForwardIcon sx={{ fontSize: 16 }} />}>
          <Button
            color="inherit"
            size="small"
            onClick={() => setSelectedDepot(null)}
            disabled={!selectedDepot}
            sx={{ textTransform: 'none', minWidth: 0, p: 0 }}
          >
            {t('common:dashboard.profits.global')}
          </Button>
          {selectedDepot && (
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {selectedDepot}
            </Typography>
          )}
        </Breadcrumbs>
      }
    >

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={viewMode} onChange={(_, val) => setViewMode(val as 'actual' | 'projected')}>
          <Tab 
            label={t('common:dashboard.profits.tabActual')} 
            value="actual" 
            icon={<TrendingUpIcon sx={{ mr: 0.5, fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab 
            label={t('common:dashboard.profits.tabProjected')} 
            value="projected"
            icon={<ForecastIcon sx={{ mr: 0.5, fontSize: 18 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {viewMode === 'actual' ? (
          <>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <TrendingUpIcon sx={{ color: POSITIVE_COLOR, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('common:dashboard.profits.metrics.sales')}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatCurrency(activeSummary.salesAmount)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <TrendingDownIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('common:dashboard.profits.metrics.cost')}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatCurrency(activeSummary.costAmount)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountTreeIcon sx={{ color: activeSummary.profit >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('common:dashboard.profits.metrics.profit')}
                    </Typography>
                  </Box>
                  <Chip
                    label={t('common:dashboard.profits.margin', { value: margin.toFixed(1) })}
                    size="small"
                    color={activeSummary.profit >= 0 ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: activeSummary.profit >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR }}>
                  {formatCurrency(activeSummary.profit)}
                </Typography>
              </Paper>
            </Grid>
          </>
        ) : (
          <>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <TrendingUpIcon sx={{ color: POSITIVE_COLOR, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('common:dashboard.profits.metrics.potentialRevenue')}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatCurrency(activeProjectedSummary.potentialRevenue)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <TrendingDownIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('common:dashboard.profits.metrics.cost')}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatCurrency(activeProjectedSummary.costAmount)}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountTreeIcon sx={{ color: activeProjectedSummary.projectedProfit >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('common:dashboard.profits.metrics.projected')}
                    </Typography>
                  </Box>
                  <Chip
                    label={t('common:dashboard.profits.margin', { value: projectedMargin.toFixed(1) })}
                    size="small"
                    color={activeProjectedSummary.projectedProfit >= 0 ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: activeProjectedSummary.projectedProfit >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR }}>
                  {formatCurrency(activeProjectedSummary.projectedProfit)}
                </Typography>
              </Paper>
            </Grid>
          </>
        )}
      </Grid>

      <ProfitDrilldownChart
        chartRows={chartRows}
        viewMode={viewMode}
        selectedDepot={selectedDepot}
        onSelectDepot={setSelectedDepot}
        formatTooltipValue={formatTooltipValue}
        t={t}
      />
    </DashboardWidget>
  );
};

export default ProfitDrilldown;